import axios from './index';
import { gql } from '@apollo/client';
import { store } from '../index';

export const GET_PATIENT_HEALTH = gql`
    query GET_PATIENT_HEALTH($id: ID!) {
        patient(id: $id) {
            id
            weight
            height
            drugAllergy
            chronicDeseases1
            chronicDeseases2
            pregnant
            smoker
            drinker
            familyMedicalRecord
            emergencyContractNumber
            emergencyContractName
            emergencyContractRelation
            published_at
        }
    }
`;

export const GET_PATIENT_PROFILE = gql`
    query GET_PATIENT_PROFILE($id: ID!) {
        user(id: $id) {
            id
            username
            fullname
            gender
            birthday
            mobileNumber
            email
            IDCardNumber
            patients(where: { user_null: false }) {
                HNCode
            }
        }
    }
`;

export const DELETE_USER_AVATAR = gql`
    mutation DELETE_USER_AVATAR($id: ID!) {
        updateUser(input: { where: { id: $id }, data: { avatar: null } }) {
            user {
                id
                avatar {
                    id
                }
            }
        }
    }
`;

export const GET_SESSIONS = gql`
    query GET_SESSIONS($id: ID!) {
        sessions(
            sort: "date:desc"
            where: { sessionEnd: true, cancelled: false, patient_summaries: { patient: $id } }
        ) {
            id
            name
            description
            date
            startTime
            endTime
            sessionEnd
            cancelled
            meet {
                id
                type
                startTime
                endTime
                sessionType
                name
                staff {
                    id
                    user {
                        fullname
                        avatar {
                            url
                        }
                    }
                    specialist
                    staff_position {
                        name
                    }
                }
            }
            session_notes {
                description
            }
            patient_summaries(where: { patient: $id }) {
                homework
                noteToPatient
                joinTime
                leaveTime
                totalTime
                diagnosis
                myNote
                patient {
                    id
                }
                staff {
                    id
                    user {
                        fullname
                        avatar {
                            url
                        }
                    }
                    specialist
                    staff_position {
                        name
                    }
                }
            }
        }
    }
`;

export const GET_HOSPITAL = gql`
    query GET_HOSPITAL($id: ID!) {
        hospital(id: $id) {
            avatar {
                url
            }
            name
            hospitalID
            email
            phoneNumber
            address
        }
    }
`;

export const GET_SESSION_STATUS = gql`
    query GET_SESSION_STATUS($id: ID!) {
        session(id: $id) {
            id
            startTime
            endTime
            actualStartTime
            actualEndTime
            sessionEnd
            cancelled
            patient_summaries {
                id
                checkinTime
                joinTime
                leaveTime
                totalTime
                patient {
                    id
                }
            }
            meet {
                type
                cancelled
                sessionType
                name
                patients(where: { user_null: false }) {
                    id
                    user {
                        id
                        fullname
                        avatar {
                            url
                        }
                    }
                }
                staff {
                    id
                    user {
                        id
                        fullname
                        avatar {
                            url
                        }
                    }
                }
                otherStaffs(where: { user_null: false }) {
                    id
                    user {
                        id
                        fullname
                        avatar {
                            url
                        }
                    }
                }
            }
        }
    }
`;

export const GET_SESSION = gql`
    query GET_SESSION($id: ID!) {
        session(id: $id) {
            id
            name
            description
            date
            startTime
            endTime
            actualStartTime
            actualEndTime
            sessionEnd
            cancelled
            roomId
            meet {
                id
                name
                type
                cancelled
                sessionType
                staff {
                    id
                    user {
                        id
                        fullname
                        avatar {
                            url
                        }
                    }
                    specialist
                    staff_position {
                        name
                    }
                }
                sessions {
                    id
                    sessionEnd
                    patient_summaries {
                        checkinTime
                    }
                }
            }
            session_notes {
                description
            }
            patient_summaries {
                id
                homework
                noteToPatient
                joinTime
                leaveTime
                totalTime
                checkinTime
                diagnosis
                myNote
                patient {
                    id
                }
                staff {
                    id
                    user {
                        fullname
                        avatar {
                            url
                        }
                    }
                    specialist
                    staff_position {
                        name
                    }
                }
            }
        }
    }
`;

export const GET_SESSION_DETAIL = gql`
    query GET_SESSION_DETAIL($id: ID!, $patient: ID) {
        session(id: $id) {
            id
            sessionEnd
            cancelled
            roomId
            actualEndTime
            patient_summaries(where: { patient: $patient }) {
                id
                checkinTime
                joinTime
                leaveTime
                myNote
                patient {
                    id
                }
            }
            meet {
                id
                name
                description
                type
                cancelled
                sessionType
                startDate
                endDate
                startTime
                endTime
                rules
                numberOfParticipants
                staff {
                    id
                    specialist
                    user {
                        fullname
                        avatar {
                            url
                        }
                    }
                }
            }
        }
    }
`;

export const GET_PUBLIC_OPEN_SESSIONS = gql`
    query GET_PUBLIC_OPEN_SESSIONS($date: Date!, $limit: Int) {
        sessions(
            where: {
                date_gte: $date
                sessionEnd: false
                cancelled: false
                meet: { type: "open", sessionType: "public" }
            }
            sort: "date:asc"
            limit: $limit
        ) {
            id
            name
            date
            startTime
            endTime
            sessionEnd
            cancelled
            meet {
                id
                name
                startDate
                endDate
                startTime
                endTime
                numberOfParticipants
                type
                sessionType
                staff {
                    user {
                        fullname
                        avatar {
                            url
                        }
                    }
                }
                hospital {
                    id
                    name
                }
            }
        }
    }
`;

export const GET_NEXT_APPOINTMENT = gql`
    query GET_NEXT_APPOINTMENT($id: ID!, $date: Date!, $limit: Int) {
        sessions(
            limit: $limit
            sort: "date:asc,startTime:asc"
            where: {
                date_gte: $date
                sessionEnd: false
                cancelled: false
                patient_summaries: { patient: $id }
            }
        ) {
            id
            date
            startTime
            endTime
            actualStartTime
            actualEndTime
            sessionEnd
            cancelled
            meet {
                name
                numberOfParticipants
                type
                cancelled
                sessionType
                patients(where: { user_null: false }) {
                    id
                }
                staff {
                    id
                    user {
                        id
                        fullname
                        avatar {
                            url
                        }
                    }
                }
                hospital {
                    name
                }
            }
        }
    }
`;

export const GET_PRESENT_SESSION = gql`
    query GET_PRESENT_SESSION($id: ID!) {
        sessions(
            limit: 1
            sort: "actualEndTime:desc"
            where: {
                patient_summaries: { patient: $id }
                sessionEnd: true
                cancelled: false
                meet: { type_in: ["one", "group"] }
            }
        ) {
            id
            date
            startTime
            endTime
            actualStartTime
            actualEndTime
            sessionEnd
            cancelled
            meet {
                type
                staff {
                    id
                    user {
                        fullname
                        avatar {
                            url
                        }
                    }
                }
            }
            patient_summaries(
                where: {
                    patient: $id
                    joinTime_null: false
                    leaveTime_null: false
                    totalTime_null: false
                }
            ) {
                id
                patient {
                    id
                }
                noteToPatient
                homework
                joinTime
                leaveTime
                totalTime
            }
        }
    }
`;

export const GET_ENDED_SESSIONS = gql`
    query GET_ENDED_SESSIONS($id: ID!) {
        sessions(
            sort: "actualEndTime:desc"
            where: { patient_summaries: { patient: $id }, sessionEnd: true, cancelled: false }
        ) {
            id
            date
            startTime
            endTime
            actualStartTime
            actualEndTime
            sessionEnd
            meet {
                name
                type
                staff {
                    id
                    user {
                        id
                        fullname
                        avatar {
                            url
                        }
                    }
                }
                hospital {
                    name
                }
            }
            patient_summaries(where: { patient: $id }) {
                id
                noteToPatient
                homework
                myNote
                diagnosis
                treatment
                patient {
                    id
                }
            }
        }
    }
`;

export const GET_ENDED_SESSION = gql`
    query GET_ENDED_SESSION($id: ID!, $patientId: ID!) {
        session(id: $id) {
            id
            date
            startTime
            endTime
            actualStartTime
            actualEndTime
            sessionEnd
            meet {
                name
                type
                staff {
                    id
                    user {
                        id
                        fullname
                        avatar {
                            url
                        }
                    }
                }
                hospital {
                    name
                }
            }
            patient_summaries(where: { patient: $patientId }) {
                id
                noteToPatient
                homework
                myNote
                diagnosis
                treatment
                patient {
                    id
                }
            }
        }
    }
`;

export const UPDATE_MY_NOTE = gql`
    mutation UPDATE_MY_NOTE($id: ID!, $myNote: String!) {
        updatePatientSummary(input: { where: { id: $id }, data: { myNote: $myNote } }) {
            patientSummary {
                id
                checkinTime
                myNote
            }
        }
    }
`;

export const UPDATE_PATIENT_HEALTH = gql`
    mutation UPDATE_PATIENT_HEALTH(
        $id: ID!
        $weight: Float!
        $height: Float!
        $drugAllergy: String
        $chronicDeseases1: String
        $chronicDeseases2: String
        $pregnant: Boolean
        $smoker: Boolean
        $drinker: Boolean
        $familyMedicalRecord: String
        $emergencyContractNumber: String
        $emergencyContractName: String
        $emergencyContractRelation: String
    ) {
        updatePatient(
            input: {
                where: { id: $id }
                data: {
                    weight: $weight
                    height: $height
                    drugAllergy: $drugAllergy
                    chronicDeseases1: $chronicDeseases1
                    chronicDeseases2: $chronicDeseases2
                    pregnant: $pregnant
                    smoker: $smoker
                    drinker: $drinker
                    familyMedicalRecord: $familyMedicalRecord
                    emergencyContractNumber: $emergencyContractNumber
                    emergencyContractName: $emergencyContractName
                    emergencyContractRelation: $emergencyContractRelation
                }
            }
        ) {
            patient {
                weight
                height
                drugAllergy
                chronicDeseases1
                chronicDeseases2
                pregnant
                smoker
                drinker
                familyMedicalRecord
                emergencyContractNumber
                emergencyContractName
                emergencyContractRelation
                published_at
            }
        }
    }
`;

export const UPDATE_PATIENT_PROFILE = gql`
    mutation UPDATE_PATIENT_PROFILE(
        $id: ID!
        $fullname: String!
        $gender: String!
        $email: String!
        $birthday: Date
        $mobileNumber: String!
    ) {
        updateUser(
            input: {
                where: { id: $id }
                data: {
                    fullname: $fullname
                    gender: $gender
                    email: $email
                    birthday: $birthday
                    mobileNumber: $mobileNumber
                }
            }
        ) {
            user {
                username
                fullname
                gender
                email
                birthday
                mobileNumber
            }
        }
    }
`;

export const GET_STAFF = gql`
    query GET_STAFF($id: ID!) {
        staff(id: $id) {
            id
            specialist
            staff_role {
                name
            }
            staff_position {
                name
            }
            title1 {
                name
            }
            title2 {
                name
            }
            user {
                fullname
                avatar {
                    url
                }
            }
        }
    }
`;

export const GET_PATIENT_UNREAD_MESSAGES_COUNT = gql`
    query GET_PATIENT_UNREAD_MESSAGES_COUNT($id: ID!) {
        patient(id: $id) {
            messages(
                where: { senderRole_in: ["Provider", "Provider Admin", "Admin"], isRead: false }
            ) {
                id
            }
        }
    }
`;

export const GET_PATIENT_MESSAGES = gql`
    query GET_PATIENT_MESSAGES($id: ID!) {
        patient(id: $id) {
            id
            messages {
                id
                createdAt
                sender
                receiver
                text
                image {
                    url
                }
                isRead
                senderRole
                staff {
                    id
                    user {
                        fullname
                        avatar {
                            url
                        }
                    }
                }
                patient {
                    id
                    user {
                        fullname
                        avatar {
                            url
                        }
                    }
                }
            }
        }
    }
`;

export const checkIn = async (data) => {
    const res = await axios.post(`/patient-summaries/check-in`, data, {
        headers: {
            authorization: `Bearer ${store.getState().strapiToken}`,
        },
    });
    return res;
};

export const canJoin = async (data) => {
    const res = await axios.post(`/open/can-join`, data, {
        headers: {
            authorization: `Bearer ${store.getState().strapiToken}`,
        },
    });
    return res;
};
